import { useMemo } from 'react'
import { Sensor } from '@/features/dashboard/models'
import { SortItem } from '@/core/models/types'
import { SORT_OPTIONS } from '../dashboardTable/TableBar'
import { infinity } from '@equinor/eds-icons'
import { useGetDevices } from '@/core/hooks-and-services/devicesService'

interface UseSortSensorsProps {
    filteredSensors: Sensor[]
    sortSpecification: {
        tag: SortItem
        installationDate: SortItem
        device: SortItem
        sensorStatus: SortItem
        batteryCapacity: SortItem
        signalStrength: SortItem
    }
}

const useSortSensors = ({ filteredSensors, sortSpecification }: UseSortSensorsProps) => {
    const { data: devices } = useGetDevices()
    const filteredDevices = devices.filter((device) => device.portfolio)
    return useMemo(() => {
        const sensorsToSort = [...filteredSensors]

        if (sortSpecification.tag?.selected) {
            sensorsToSort.sort((a, b) => {
                const aTag = a.tag ?? ''
                const bTag = b.tag ?? ''
                return sortSpecification.tag?.selected === SORT_OPTIONS.tag.first
                    ? aTag.localeCompare(bTag)
                    : bTag.localeCompare(aTag)
            })
        } else if (sortSpecification.installationDate?.selected) {
            sensorsToSort.sort((a, b) => {
                const dateA = a.installationDate ? new Date(a.installationDate).getTime() : null
                const dateB = b.installationDate ? new Date(b.installationDate).getTime() : null

                if (sortSpecification.installationDate?.selected === SORT_OPTIONS.installationDate.first) {
                    return (dateB ?? 0) - (dateA ?? 0)
                } else {
                    return (dateA ?? 0) - (dateB ?? 0)
                }
            })
        } else if (sortSpecification.device?.selected) {
            sensorsToSort.sort((a, b) => {
                const aDevice = filteredDevices.find((d) => d.key === a.sensorModel)
                const bDevice = filteredDevices.find((d) => d.key === b.sensorModel)
                const aDisplayName = aDevice?.displayName ?? ''
                const bDisplayName = bDevice?.displayName ?? ''
                return sortSpecification.device?.selected === SORT_OPTIONS.device.first
                    ? aDisplayName.localeCompare(bDisplayName)
                    : bDisplayName.localeCompare(aDisplayName)
            })
        } else if (sortSpecification.sensorStatus?.selected) {
            sensorsToSort.sort((a, b) => {
                const aSensorStatus = a.status ?? ''
                const bSensorStatus = b.status ?? ''
                return sortSpecification.sensorStatus?.selected === SORT_OPTIONS.sensorStatus.first
                    ? aSensorStatus.localeCompare(bSensorStatus)
                    : bSensorStatus.localeCompare(aSensorStatus)
            })
        } else if (sortSpecification.batteryCapacity?.selected) {
            sensorsToSort.sort((a, b) => {
                const aBatteryCapacity = a.battery?.value ?? 0
                const bBatteryCapacity = b.battery?.value ?? 0
                return sortSpecification.batteryCapacity?.selected === SORT_OPTIONS.batteryCapacity.first
                    ? aBatteryCapacity - bBatteryCapacity
                    : bBatteryCapacity - aBatteryCapacity
            })
        } else if (sortSpecification.signalStrength?.selected) {
            sensorsToSort.sort((a, b) => {
                const aRssi = a.rssi?.value ?? -Infinity
                const bRssi = b.rssi?.value ?? -Infinity
                return sortSpecification.signalStrength?.selected === SORT_OPTIONS.signalStrength.first
                    ? aRssi - bRssi
                    : bRssi - aRssi
            })
        }
        return sensorsToSort
    }, [filteredSensors, sortSpecification])
}

export default useSortSensors
